interface CruiseValuedUpTotals {
  value: number
  memberValue: number
}

/**
 * Compute cabin pricing data
 *
 * @remarks
 * Pricing data comes from 2 sources:
 *  - Cabin Pricing
 *  - Cabin Selection
 *
 *  Cabin Pricing takes priority
 */
export function computeCabinPricing(
  cabinSelectionData: App.Cruises.BookingCabinSelectionData,
  cabinPricingData: App.Cruises.BookingCabinPricingData,
  ratePriceDetails?: App.Cruises.BookingCabinRatePriceDetails,
): App.Checkout.ItemViewTotals {
  const { cabinSelection } = cabinSelectionData
  const { cabinPricing } = cabinPricingData

  if (cabinPricing) {
    const pricing = cabinPricing.pricing
    const memberPrice = pricing.total.memberPrice ?? 0
    const price = pricing.total.amount
    const { value, memberValue } = computeValuedUpPrice(ratePriceDetails, pricing)

    return {
      taxesAndFees: pricing.tax?.amount ?? 0,
      price,
      memberPrice,
      value,
      memberValue,
      surcharge: 0,
      extraGuestSurcharge: 0,
    }
  }
  else if (cabinSelection) {
    const { pricing } = cabinSelection
    const memberPrice = pricing.total.memberPrice ?? 0
    const price = pricing.total.amount
    const { value, memberValue } = computeValuedUpPrice(ratePriceDetails, pricing)

    return {
      taxesAndFees: pricing.tax?.amount ?? 0,
      price,
      memberPrice,
      value,
      memberValue,
      surcharge: 0,
      extraGuestSurcharge: 0,
    }
  }
  else {
    return {
      taxesAndFees: 0,
      price: 0,
      memberPrice: 0,
      value: 0,
      memberValue: 0,
      surcharge: 0,
      extraGuestSurcharge: 0,
    }
  }
}

/**
 * Brochure cabin total (per-person rate discount anchor × passengers) vs public and member cabin totals from `pricing`.
 */
function computeValuedUpPrice(
  ratePriceDetails: App.Cruises.BookingCabinRatePriceDetails | undefined,
  pricing: App.Cruises.BookingCabinPricingDetails,
): CruiseValuedUpTotals {
  const pax = passengerCountFromBookingPricing(pricing)
  const publicTotal = Math.round(pricing.total.amount)
  const memberTotal = Math.round(pricing.total.memberPrice ?? 0)

  const valuedUpPerPersonAmount = ratePriceDetails?.discountDetails?.amount ?? 0
  const valuedUpCabinTotal = Math.round(valuedUpPerPersonAmount * pax)

  const value = valuedUpCabinTotal > publicTotal ? valuedUpCabinTotal : 0
  const memberValue =
    memberTotal > 0 && valuedUpCabinTotal > memberTotal ? valuedUpCabinTotal : 0

  return { value, memberValue }
}

function passengerCountFromBookingPricing(pricing: App.Cruises.BookingCabinPricingDetails): number {
  const n = pricing.passengerPricing?.length ?? 0
  return n > 0 ? n : 1
}
